import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Divider,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  MenuBook as BookIcon,
  PhotoCamera as CameraIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useRecipes } from '../context/RecipeContext';
import { ROUTES } from '../utils/constants';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { BookContainer, BookPage, BookNavigation, WelcomeTour } from '../components';
import { getApiKey, fetchKeySpendInfo } from '../services/authService';
import { useFirstTimeUser } from '../hooks/useFirstTimeUser';
import { SEO } from '../utils/seo/SEO';
import { generateOrganizationSchema, generateWebApplicationSchema } from '../utils/seo/structuredData';

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { fetchRecipes, totalRecipes } = useRecipes();
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [loadingTokens, setLoadingTokens] = useState<boolean>(true);
  const { isFirstRun, hasSeenWelcomeTour, completeWelcomeTour } = useFirstTimeUser();
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  useEffect(() => {
    fetchRecipes();
    fetchTokenBalance();
  }, [fetchRecipes]);

  useEffect(() => {
    // Show welcome tour if first run and hasn't seen tour yet
    if (isFirstRun && !hasSeenWelcomeTour) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setShowWelcomeTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isFirstRun, hasSeenWelcomeTour]);

  const fetchTokenBalance = async () => {
    try {
      setLoadingTokens(true);
      const apiKey = await getApiKey();
      const balance = await fetchKeySpendInfo(apiKey);
      setTokenBalance(balance);
    } catch (err) {
      console.error('Error fetching token balance:', err);
      // Set balance to null on error to show a graceful fallback
      setTokenBalance(null);
    } finally {
      setLoadingTokens(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <>
      <SEO
        title="AI Cooking Assistant - Smart Recipe Management"
        description="Cook with Otto is your free AI cooking assistant for easy meal planning. Get personalized recipes, step-by-step cooking guidance, and organize your favorite dishes. Start cooking smarter today!"
        url="/home"
        structuredData={[generateOrganizationSchema(), generateWebApplicationSchema()]}
        keywords={[
          'AI cooking assistant',
          'recipe app',
          'meal planning app',
          'cooking guidance',
          'recipe management',
          'smart cooking',
          'AI recipes',
          'cooking help',
        ]}
      />
      <BookContainer>
        <Box sx={{
          width: '100%',
          py: 4,
        }}>
        {/* Two-page book spread */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: 0,
          maxWidth: { xs: '100%', md: 1200 },
          margin: '0 auto',
        }}>
          {/* Left Page - Welcome/Decorative */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            minHeight: { xs: '50vh', md: 'auto' },
          }}>
            <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.HOME_LEFT}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 4 },
              }}>
                {/* Welcome Content */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, type: 'spring' }}
                >
                  <BookIcon
                    sx={{
                      fontSize: { xs: 60, md: 80 },
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.8)'
                        : 'rgba(139, 69, 19, 0.8)',
                      mb: { xs: 2, md: 3 },
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.9)' 
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 2,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    Welcome to
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.9)' 
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 3,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                      textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    My Kitchen
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontStyle: 'italic',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.7)' 
                        : 'rgba(139, 69, 19, 0.7)',
                      mb: 2,
                      fontSize: '1.2rem',
                    }}
                  >
                    {currentUser?.displayName ? `Chef ${currentUser.displayName}'s` : 'Your'}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontStyle: 'italic',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.7)' 
                        : 'rgba(139, 69, 19, 0.7)',
                      mb: 4,
                      fontSize: '1.2rem',
                    }}
                  >
                    Personal Collection
                  </Typography>
                </motion.div>

                {/* Decorative Divider */}
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 150, opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                >
                  <Divider 
                    sx={{ 
                      mb: 4,
                      width: 150,
                      height: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, transparent, rgba(255, 248, 240, 0.4), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(139, 69, 19, 0.4), transparent)',
                      border: 'none',
                    }} 
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.8 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.6)'
                        : 'rgba(139, 69, 19, 0.6)',
                      mb: 3,
                    }}
                  >
                    {totalRecipes} delicious recipes awaiting
                  </Typography>
                </motion.div>

                {/* Green Tokens Section at bottom */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0, duration: 0.8 }}
                  style={{
                    paddingTop: '20px',
                    width: '100%',
                    marginTop: 'auto', // Valid CSS property, but type checker complains for MotionStyle sometimes if mixed.
                    // Using display: flex on parent usually handles this better, but keeping style object simpler.
                  } as any} 
                >
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 248, 240, 0.05)'
                      : 'rgba(139, 69, 19, 0.05)',
                    border: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.1)' 
                      : 'rgba(139, 69, 19, 0.1)'}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="img"
                        src="/assets/images/greentokens.png"
                        alt="Green Tokens"
                        sx={{
                          width: 20,
                          height: 20,
                          opacity: 0.8,
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontFamily: '"Crimson Text", serif',
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.7)' 
                            : 'rgba(139, 69, 19, 0.7)',
                          fontSize: '0.75rem',
                        }}
                      >
                        Green Tokens
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {loadingTokens ? (
                        <CircularProgress size={14} sx={{
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.4)'
                            : 'rgba(139, 69, 19, 0.4)'
                        }} />
                      ) : (
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: '"Playfair Display", serif',
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.8)'
                              : 'rgba(139, 69, 19, 0.8)',
                            fontSize: '1rem',
                          }}
                        >
                          {tokenBalance !== null ? tokenBalance.toFixed(2) : '-.--'}
                        </Typography>
                      )}
                      
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => window.open('https://buy.stripe.com/test_28o02t5LT4Yo5mocMM', '_blank')}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0.25,
                          px: 1,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontFamily: '"Crimson Text", serif',
                          borderColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.3)' 
                            : 'rgba(139, 69, 19, 0.3)',
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.7)' 
                            : 'rgba(139, 69, 19, 0.7)',
                          '&:hover': {
                            borderColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.5)' 
                              : 'rgba(139, 69, 19, 0.5)',
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.05)' 
                              : 'rgba(139, 69, 19, 0.05)',
                          },
                        }}
                      >
                        Buy More
                      </Button>
                    </Box>
                  </Box>
                </motion.div>
              </Box>
            </BookPage>
          </Box>

          {/* Right Page - Navigation Buttons */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            minHeight: { xs: '50vh', md: 'auto' },
          }}>
            <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.HOME_RIGHT}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 4 },
              }}>

                {/* Section Title */}
                <motion.div
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.9)' 
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 4,
                      textAlign: 'center',
                    }}
                  >
                    Choose Your Path
                  </Typography>
                </motion.div>

                {/* Three Main Navigation Buttons */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.5, md: 3 },
                  width: '100%',
                  maxWidth: 350,
                  justifyContent: 'center',
                }}>
                  {/* My Recipes Button */}
                  <motion.div
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                  >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleNavigation(ROUTES.RECIPES)}
                    component={motion.button}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    sx={{
                      width: '100%',
                      minHeight: { xs: 60, md: 80 },
                      fontFamily: '"Crimson Text", serif',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      px: { xs: 2, md: 3 },
                      py: { xs: 1.5, md: 2 },
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.2)'
                        : 'rgba(139, 69, 19, 0.8)',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(255, 248, 240, 0.95)',
                      border: `2px solid ${theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.4)'
                        : 'rgba(139, 69, 19, 0.6)'}`,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.3)'
                          : 'rgba(139, 69, 19, 0.9)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <BookIcon sx={{ fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                    My Recipes
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', opacity: 0.8 }}>
                      Browse collection
                    </Typography>
                  </Button>
                  </motion.div>

                  {/* Capture Food Button */}
                  <motion.div
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                  >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleNavigation(ROUTES.RECIPE_CAPTURE)}
                    component={motion.button}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    sx={{
                      width: '100%',
                      minHeight: { xs: 60, md: 80 },
                      fontFamily: '"Crimson Text", serif',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      px: { xs: 2, md: 3 },
                      py: { xs: 1.5, md: 2 },
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.25)'
                        : 'rgba(160, 82, 45, 0.8)',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(255, 248, 240, 0.95)',
                      border: `2px solid ${theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.4)'
                        : 'rgba(160, 82, 45, 0.6)'}`,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.35)'
                          : 'rgba(160, 82, 45, 0.9)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <CameraIcon sx={{ fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                    Capture Food
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', opacity: 0.8 }}>
                      Add new recipe
                    </Typography>
                  </Button>
                  </motion.div>

                  {/* Kitchen Settings Button */}
                  <motion.div
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.8 }}
                  >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleNavigation(ROUTES.SETTINGS)}
                    component={motion.button}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    sx={{
                      width: '100%',
                      minHeight: { xs: 60, md: 80 },
                      fontFamily: '"Crimson Text", serif',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      px: { xs: 2, md: 3 },
                      py: { xs: 1.5, md: 2 },
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.15)'
                        : 'rgba(101, 67, 33, 0.8)',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.9)'
                        : 'rgba(255, 248, 240, 0.95)',
                      border: `2px solid ${theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.4)'
                        : 'rgba(101, 67, 33, 0.6)'}`,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.25)'
                          : 'rgba(101, 67, 33, 0.9)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                    Kitchen Settings
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', opacity: 0.8 }}>
                      Preferences
                    </Typography>
                  </Button>
                  </motion.div>
                </Box>

                {/* Copyright/Attribution */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.8 }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute',
                      bottom: 40,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontFamily: '"Crimson Text", serif',
                      fontSize: '0.9rem',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.4)' 
                        : 'rgba(139, 69, 19, 0.4)',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    © 2024 - Crafted with love
                  </Typography>
                </motion.div>
              </Box>
            </BookPage>
          </Box>
        </Box>
      </Box>

      {/* Book Navigation */}
      <BookNavigation currentPageNumber={PAGE_NUMBERS.HOME_LEFT} />

      {/* Welcome Tour */}
      <WelcomeTour
        open={showWelcomeTour}
        onComplete={() => {
          completeWelcomeTour();
          setShowWelcomeTour(false);
        }}
        onSkip={() => {
          completeWelcomeTour();
          setShowWelcomeTour(false);
        }}
      />
    </BookContainer>
    </>
  );
};

export default HomeScreen;
